import { useState } from "react";
import {
  setThemeColor,
  deleteThemeColor,
  renameThemeColor,
  generateAssetUploadUrl,
  uploadAssetToS3,
  setThemeFont,
  deleteThemeFont,
  setThemeIcon,
  deleteThemeIcon,
} from "../../api/widgetThemeApi";
import { getV2EditTarget } from "../../api/widgetThemeApiV2";
import {
  UseThemeHandlersParams,
  UseThemeHandlersReturn,
  SelectedFont,
  SelectedIcon,
  PendingDelete,
} from "./types";
import {
  buildGoogleFontUrl,
  getAvailableWeightsForFont,
} from "./data/googleFonts";

/** V1 themeId or V2 edit-target URL params. */
const canWriteTheme = (themeId: string | null | undefined, editThemeToken: string | null | undefined) =>
  Boolean(editThemeToken && (themeId || getV2EditTarget()));

/**
 * Normalize a color value to 8-char RGBA hex for internal use
 * Handles both 6-char (#RRGGBB) and 8-char (#RRGGBBAA) formats
 */
const normalizeToRgba = (color: string): string => {
  if (!color) return "#000000ff";
  const normalized = color.startsWith("#") ? color : `#${color}`;
  if (normalized.length === 9) {
    return normalized;
  }
  if (normalized.length === 7) {
    return `${normalized}ff`;
  }
  return normalized.padEnd(9, "f").slice(0, 9);
};

/**
 * Normalize a color value for storage (remove `ff` alpha if fully opaque)
 * Returns 6-char hex if alpha is `ff`, otherwise 8-char hex
 */
const normalizeForStorage = (color: string): string => {
  const rgba = normalizeToRgba(color);
  // If alpha is `ff` (fully opaque), return 6-char hex
  if (rgba.length === 9 && rgba.slice(7, 9).toLowerCase() === "ff") {
    return rgba.slice(0, 7);
  }
  // Otherwise return 8-char hex
  return rgba;
};
import {
  THEME_SYNC_MESSAGE_TYPE,
  ThemeSyncPayload,
} from "../../hooks/useWidgetTheme";

// Helper to notify widgets of theme changes via postMessage
// In iframe mode: posts to the iframe window
// In inline mode: posts to the same window so other widgets can refetch
const notifyOfThemeUpdate = (
  iframeRef: React.RefObject<HTMLIFrameElement | null> | null,
  updateType: ThemeSyncPayload["updateType"],
  themeId: string
) => {
  const message = {
    type: THEME_SYNC_MESSAGE_TYPE,
    payload: { updateType, themeId },
  };

  if (iframeRef?.current?.contentWindow) {
    // Iframe mode: post to iframe
    iframeRef.current.contentWindow.postMessage(
      message,
      window.location.origin
    );
  } else {
    // Inline mode: post to same window so other widgets can hear and refetch
    window.postMessage(message, window.location.origin);
  }
};

// Helper to get font format from filename
const getFontFormat = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "woff2":
      return "woff2";
    case "woff":
      return "woff";
    case "ttf":
      return "truetype";
    case "otf":
      return "opentype";
    default:
      return "woff2";
  }
};

export const useThemeHandlers = ({
  themeId,
  editThemeToken,
  iframeRef,
  refreshThemeColors,
  refreshThemeFonts,
  refreshThemeIcons,
}: UseThemeHandlersParams): UseThemeHandlersReturn => {
  // V2 Edit live has no themeId — API helpers accept '' and use getV2EditTarget().
  const writeThemeId = themeId || "";
  const writeToken = editThemeToken || "";

  // Loading and messages
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Color state
  const [isAddingColor, setIsAddingColor] = useState(false);
  const [newColorKey, setNewColorKey] = useState("");
  const [newColorValue, setNewColorValue] = useState("#000000ff");

  // Delete confirmation state
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null
  );

  // Color handlers
  const handleAddColor = async () => {
    if (!newColorKey.trim()) {
      setError("Color name is required");
      return;
    }

    if (!canWriteTheme(themeId, editThemeToken)) {
      setError("Edit theme target and token are required");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await setThemeColor({
        themeId: writeThemeId,
        colorKey: newColorKey.trim(),
        colorValue: normalizeForStorage(newColorValue),
        editThemeToken: writeToken,
      });

      // Refresh theme colors from React Query to update all widgets
      await refreshThemeColors();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "colors", writeThemeId);

      // Reset form
      setNewColorKey("");
      setNewColorValue("#000000ff");
      setIsAddingColor(false);
      setSuccessMessage("Color added successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to add color");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateColor = async (colorKey: string, newValue: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await setThemeColor({
        themeId: writeThemeId,
        colorKey,
        colorValue: normalizeForStorage(newValue),
        editThemeToken: writeToken,
      });

      // Refresh theme colors from React Query to update all widgets
      await refreshThemeColors();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "colors", writeThemeId);

      setSuccessMessage("Color updated successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to update color");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameColor = async (
    oldColorKey: string,
    newColorKey: string
  ) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;

    if (!newColorKey.trim()) {
      setError("Color name is required");
      return;
    }

    if (oldColorKey === newColorKey.trim()) {
      // No change, nothing to do
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await renameThemeColor({
        themeId: writeThemeId,
        oldColorKey,
        newColorKey: newColorKey.trim(),
        editThemeToken: writeToken,
      });

      // Refresh theme colors from React Query to update all widgets
      await refreshThemeColors();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "colors", writeThemeId);

      setSuccessMessage("Color renamed successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to rename color");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteColor = (colorKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;
    setPendingDelete({ type: "color", key: colorKey });
  };

  const executeDeleteColor = async (colorKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteThemeColor({
        themeId: writeThemeId,
        colorKey,
        editThemeToken: writeToken,
      });

      // Refresh theme colors from React Query to update all widgets
      await refreshThemeColors();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "colors", writeThemeId);

      setSuccessMessage("Color deleted successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to delete color");
    } finally {
      setIsLoading(false);
    }
  };

  // Font handler - handles system, Google, and uploaded fonts
  const handleAddFont = async (font: SelectedFont) => {
    if (!canWriteTheme(themeId, editThemeToken)) {
      setError("Edit theme target and token are required");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let fontUrl: string;
      let fontFormat: string;

      switch (font.type) {
        case "system":
          // System fonts use CSS font-family value directly
          fontUrl = font.value || font.name;
          fontFormat = "system";
          break;

        case "google":
          // Build Google Fonts CSS URL with ALL available weights
          // This makes the font library complete - weight selection happens at usage time
          const availableWeights = getAvailableWeightsForFont(font.name);
          const weights = availableWeights.map((v) => v.weight);
          // Always include weights 800 and 900 if not already present
          // (Some fonts may have these weights but they might not be in the definition)
          if (!weights.includes(800)) {
            weights.push(800);
          }
          if (!weights.includes(900)) {
            weights.push(900);
          }
          fontUrl = buildGoogleFontUrl(font.name, weights);
          fontFormat = "google";
          break;

        case "upload":
          // Upload the font file to S3
          if (!font.file) {
            throw new Error("No font file provided");
          }

          const { signedUploadUrl, objectPublicUrl } =
            await generateAssetUploadUrl({
              themeId: writeThemeId,
              fileName: font.file.name,
              contentType: font.file.type || "font/woff2",
              assetType: "font",
              editThemeToken: writeToken,
            });

          await uploadAssetToS3({
            file: font.file,
            signedUploadUrl,
          });

          fontUrl = objectPublicUrl;
          fontFormat = getFontFormat(font.file.name);
          break;

        default:
          throw new Error("Unknown font type");
      }

      // Save font to theme
      await setThemeFont({
        themeId: writeThemeId,
        fontKey: font.name.toLowerCase().replace(/\s+/g, "-"),
        fontValue: {
          name: font.name,
          url: fontUrl,
          format: fontFormat,
        },
        editThemeToken: writeToken,
      });

      await refreshThemeFonts();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "fonts", writeThemeId);

      setSuccessMessage("Font added successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to add font");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFont = (fontKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;
    setPendingDelete({ type: "font", key: fontKey });
  };

  const executeDeleteFont = async (fontKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteThemeFont({
        themeId: writeThemeId,
        fontKey,
        editThemeToken: writeToken,
      });

      await refreshThemeFonts();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "fonts", writeThemeId);

      setSuccessMessage("Font deleted successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to delete font");
    } finally {
      setIsLoading(false);
    }
  };

  // Icon handler - handles Lucide and uploaded icons
  const handleAddIcon = async (icon: SelectedIcon) => {
    if (!canWriteTheme(themeId, editThemeToken)) {
      setError("Edit theme target and token are required");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let iconUrl: string;

      switch (icon.type) {
        case "lucide":
          // Use Lucide CDN URL directly
          iconUrl = icon.url || "";
          if (!iconUrl) {
            throw new Error("No icon URL provided");
          }
          break;

        case "upload":
          // Upload the icon file to S3
          if (!icon.file) {
            throw new Error("No icon file provided");
          }

          const { signedUploadUrl, objectPublicUrl } =
            await generateAssetUploadUrl({
              themeId: writeThemeId,
              fileName: icon.file.name,
              contentType: icon.file.type || (icon.file.name.endsWith(".svg") ? "image/svg+xml" : "application/octet-stream"),
              assetType: "icon",
              editThemeToken: writeToken,
            });

          await uploadAssetToS3({
            file: icon.file,
            signedUploadUrl,
          });

          iconUrl = objectPublicUrl;
          break;

        default:
          throw new Error("Unknown icon type");
      }

      // Save icon to theme
      await setThemeIcon({
        themeId: writeThemeId,
        iconKey: icon.name.toLowerCase().replace(/\s+/g, "-"),
        iconValue: {
          name: icon.name,
          url: iconUrl,
        },
        editThemeToken: writeToken,
      });

      await refreshThemeIcons();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "icons", writeThemeId);

      setSuccessMessage("Icon added successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to add icon");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteIcon = (iconKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;
    setPendingDelete({ type: "icon", key: iconKey });
  };

  const executeDeleteIcon = async (iconKey: string) => {
    if (!canWriteTheme(themeId, editThemeToken)) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteThemeIcon({
        themeId: writeThemeId,
        iconKey,
        editThemeToken: writeToken,
      });

      await refreshThemeIcons();

      // Notify iframe to refresh its cache
      notifyOfThemeUpdate(iframeRef, "icons", writeThemeId);

      setSuccessMessage("Icon deleted successfully");
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to delete icon");
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm and execute pending delete
  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const { type, key } = pendingDelete;
    setPendingDelete(null);

    switch (type) {
      case "color":
        await executeDeleteColor(key);
        break;
      case "font":
        await executeDeleteFont(key);
        break;
      case "icon":
        await executeDeleteIcon(key);
        break;
    }
  };

  // Cancel pending delete
  const cancelDelete = () => {
    setPendingDelete(null);
  };

  return {
    // Loading and messages
    isLoading,
    error,
    successMessage,
    // Color state
    isAddingColor,
    setIsAddingColor,
    newColorKey,
    setNewColorKey,
    newColorValue,
    setNewColorValue,
    // Color Handlers
    handleAddColor,
    handleUpdateColor,
    handleRenameColor,
    handleDeleteColor,
    // Font Handlers
    handleAddFont,
    handleDeleteFont,
    // Icon Handlers
    handleAddIcon,
    handleDeleteIcon,
    // Delete confirmation
    pendingDelete,
    confirmDelete,
    cancelDelete,
  };
};
